import React, { useEffect, useRef, useState } from "react";

import {
  RideIconButton,
  RideGlUp,
  RideBadge,
  RideGlPlus,
} from "@rimac-seguros/ride-system-components";
import { useSelector } from "react-redux";
import Lottie from "lottie-react";

import { usePlansContext } from "../../../step-plans/context/PlansContext";
import { formatAmountThousand } from "@/utils/Utils";
import loadingAnimated from "@/assets/animation/loading-animated.json";
import { RATES_RESTRICTED_PERCENTAGES } from "../../constants/step-restrictions";

import confetti from "@/assets/animation/confetti.json";
import "./styles.scss";
import { formatAndMaskFullName } from '@/utils/format-name'

export const DetailPlanMobile = ({ callAnalyticsAction, shouldDisplayBeneficiaries = false }) => {
  const [open, setOpen] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);

  const { nameFrequency, typeOfFinancing, planSelected } = useSelector(
    (state) => state.plans
  );
  const { beneficiaryType, beneficiaries } = useSelector((state) => state.beneficiaries);
  const { isLoadingData } = usePlansContext();
  const contentRef = useRef(null);
  const lottieRef = useRef(null);
  const lottieConfettiRef = useRef();
  const lottieConfetti2Ref = useRef();
  const isLegalBeneficiary = beneficiaryType === "beneficiary-legal";

  const { devolucionTotal, aporte, ganancia } = typeOfFinancing || {};
  const { sumAssured, savingTime, returnPercentage } = planSelected || {};
  const descMoney = sumAssured?.descripcion;
  const maxAmountQuote = useSelector((state) => state.plans.maxAmountQuote);
  const isCreditMaxAmount = Number(sumAssured?.value) > maxAmountQuote;

  const validateRateAllowed =
    returnPercentage &&
    !RATES_RESTRICTED_PERCENTAGES.includes(Number(returnPercentage.value));

    const handleClickAction = () => {
      if (!open) {
        callAnalyticsAction();
      }
      setOpen(!open);
    };

  useEffect(() => {
    if (isLoadingData) {
      setOpen(false);
    }
  }, [isLoadingData]);

  useEffect(() => {
    if (open && contentRef?.current) {
      setContentHeight(contentRef?.current?.scrollHeight);
    }
  }, [open, returnPercentage]);

  useEffect(() => {
    if (lottieRef && lottieRef?.current) {
      lottieRef?.current?.setSpeed(1.5);
    }
  }, [lottieRef]);

  useEffect(() => {
    if (lottieConfettiRef && lottieConfettiRef?.current) {
      lottieConfettiRef?.current?.setSpeed(1.5);
    }
  }, [lottieConfettiRef]);

  useEffect(() => {
    if (lottieConfetti2Ref && lottieConfetti2Ref.current) {
      lottieConfetti2Ref?.current?.setSpeed(1.5);
    }
  }, [lottieConfetti2Ref]);

  return (
    <div className={`detail-plan-mob-ve relative col-span-full p-ride-gl-spacing-large ${isCreditMaxAmount ? 'hidden' : 'lg:hidden'}`}>
      <div
        className={`detail-plan-mob-ve_container relative overflow-hidden ${
          isLoadingData ? "blink-loading" : ""
        }`}
      >
        <div className="detail-plan-mob-ve_confetti">
          <Lottie
            lottieRef={lottieConfettiRef}
            animationData={confetti}
            autoplay
          />
          <Lottie
            lottieRef={lottieConfetti2Ref}
            animationData={confetti}
            autoplay
          />
        </div>
        <div className="flex items-center justify-between pt-ride-gl-spacing-large pb-ride-gl-spacing-xlarge">
          <div className="relative flex flex-col w-full gap-ride-gl-spacing-large">
            <div className="ride-sys-text-label-small-regular">
              <div className="detail-plan-mob-ve_value-amount relative mb-1">
                {isLoadingData ? (
                  <Lottie
                    lottieRef={lottieRef}
                    animationData={loadingAnimated}
                    autoplay
                    className="detail-plan-mob-ve_animated-amount"
                  />
                ) : (
                  <>
                    {descMoney}
                    {formatAmountThousand(
                      Number(devolucionTotal || 0).toFixed(2)
                    )}
                  </>
                )}
              </div>
              Es lo que recibirás al elegir el{" "}
              <span className="ride-sys-text-label-small-default">
                Seguro de Vida Endosable RIMAC
              </span>
            </div>
          </div>
          <RideIconButton
            glyph={<RideGlUp />}
            variant="outline"
            size="small"
            className={`detail-plan-mob-ve_button ${
              open ? "transform rotate-180 transition-all delay-75" : ""
            }`}
            onClick={handleClickAction}
          />
        </div>

        <div
          className="overflow-hidden transition-all"
          style={{
            height: open ? `${contentHeight}px` : "0px",
          }}
        >
          <div ref={contentRef}>
            {validateRateAllowed && (
              <div className="flex items-center justify-between ride-sys-text-paragraph-medium-bold py-ride-gl-spacing-large lg:py-ride-gl-spacing-xlarge px-ride-gl-spacing-2xlarge border-t-ride-gl-border-width-medium border-ride-sys-color-border-default dashed-custom lg:border-t-0">
                <div className="flex flex-col text-center">
                  <span>Aporte total</span>
                  <span>{`${descMoney}${formatAmountThousand(
                    Number(aporte || 0).toFixed(2)
                  )}`}</span>
                </div>
                <div className="flex flex-col text-center">
                  <RideGlPlus size="medium" />
                </div>
                <div className="flex flex-col text-center">
                  <span>Ganancias</span>
                  <span>{`${descMoney}${formatAmountThousand(
                    Number(ganancia || 0).toFixed(2)
                  )}`}</span>
                </div>
              </div>
            )}
            <div className={`flex flex-col gap-ride-gl-spacing-large border-t-ride-gl-border-width-thin border-ride-sys-color-border-default py-ride-gl-spacing-large lg:py-ride-gl-spacing-xlarge ${!validateRateAllowed && 'dashed-custom'}`}>
              <div className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                <span>Tiempo de ahorro</span>
                <span>{`${savingTime?.text} años`}</span>
              </div>
              <div className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                <span>Porcentaje de devolución</span>
                <RideBadge
                  case="promotional"
                  primaryText={returnPercentage?.text}
                />
              </div>
            </div>
            <div className="flex flex-col gap-ride-gl-spacing-large border-t-ride-gl-border-width-thin border-ride-sys-color-border-default py-ride-gl-spacing-large lg:py-ride-gl-spacing-xlarge">
              <div className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                <span>Cobertura por fallecimiento</span>
                <span>{sumAssured?.text}</span>
              </div>
              <div className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                <span>Frecuencia de pago</span>
                <span>{nameFrequency}</span>
              </div>
            </div>
            {
              shouldDisplayBeneficiaries && beneficiaryType && (
                <div className="flex flex-col gap-ride-gl-spacing-large border-t-ride-gl-border-width-thin border-ride-sys-color-border-default py-ride-gl-spacing-large lg:py-ride-gl-spacing-xlarge">
                  {
                    isLegalBeneficiary ? (
                      <>
                        <p className="ride-sys-text-paragraph-small-bold">Beneficiarios</p>
                        <p className='ride-sys-text-paragraph-medium-regular-short'>Herederos legales</p>
                      </>
                    ) : (
                      <>
                        <p className="ride-sys-text-paragraph-small-bold">Beneficiarios ({beneficiaries.length})</p>
                        {
                          beneficiaries.map(beneficiary => (
                            <div key={beneficiary?.documentNumber} className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                              <span>{formatAndMaskFullName(beneficiary.names, beneficiary.lastName, beneficiary.surName)}</span>
                              <span>{beneficiary.percentage}%</span>
                            </div>
                          ))
                        }
                      </>
                    )
                  }
                </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
};
